import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { PropertyCard } from "@/components/PropertyCard";
import { listerLabel } from "@/data/constants";
import { Loader2, ShieldCheck, Briefcase, Building2 } from "lucide-react";

export default function Agents() {
  const { id } = useParams();
  return id ? <AgentProfile id={id} /> : <AgentDirectory />;
}

function RoleTag({ agent }) {
  const label = agent.verification_role ? listerLabel(agent.verification_role) : agent.role === "agent" ? "Agent" : "Lister";
  return <span className="rounded-full bg-[#F7F5F0] px-2.5 py-1 text-xs text-[#2C3830]">{label}</span>;
}

function AgentDirectory() {
  const [agents, setAgents] = useState(null);

  useEffect(() => {
    api.get("/agents").then(({ data }) => setAgents(data)).catch(() => setAgents([]));
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
      <span className="label-caps text-[#D4A373]">Trusted professionals</span>
      <h1 className="mt-2 font-display text-3xl font-light text-[#2C3830] sm:text-4xl">Agents & Property Managers</h1>
      <p className="mt-3 max-w-2xl text-sm text-[#6B7280] sm:text-base">
        Browse real estate agents, brokers and property managers listing multiple units on RENTO. Verified professionals carry a trust badge.
      </p>

      {agents === null ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#2C3830]" /></div>
      ) : agents.length === 0 ? (
        <p data-testid="agents-empty" className="py-16 text-center text-sm text-[#6B7280]">No agents or property managers yet.</p>
      ) : (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {agents.map((a) => (
            <Link key={a.user_id} to={`/agents/${a.user_id}`} data-testid={`agent-card-${a.user_id}`} className="group rounded-2xl border border-border/60 bg-white p-5 transition-shadow hover:shadow-md">
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-[#2C3830] text-white">
                  {a.picture ? <img src={a.picture} alt={a.name} className="h-full w-full object-cover" /> : <Briefcase className="h-5 w-5" />}
                </span>
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 truncate font-medium text-[#2C3830]">
                    {a.name}
                    {a.is_verified && <ShieldCheck className="h-4 w-4 shrink-0 text-[#1D4ED8]" />}
                  </p>
                  <RoleTag agent={a} />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm text-[#6B7280]">
                <Building2 className="h-4 w-4 text-[#D4A373]" /> {a.listings_count} active listing{a.listings_count === 1 ? "" : "s"}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function AgentProfile({ id }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/agents/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [id]);

  if (data === null) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#2C3830]" /></div>;
  if (data === false) return <p className="py-20 text-center text-sm text-[#6B7280]">Agent not found.</p>;

  const { agent, listings } = data;
  return (
    <div className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
      <Link to="/agents" className="text-sm text-[#6B7280] hover:text-[#2C3830]">← All agents</Link>
      <div className="mt-4 flex items-center gap-4">
        <span className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-[#2C3830] text-white">
          {agent.picture ? <img src={agent.picture} alt={agent.name} className="h-full w-full object-cover" /> : <Briefcase className="h-6 w-6" />}
        </span>
        <div>
          <h1 data-testid="agent-name" className="flex items-center gap-2 font-display text-2xl font-medium text-[#2C3830] sm:text-3xl">
            {agent.name}
            {agent.is_verified && <ShieldCheck className="h-5 w-5 text-[#1D4ED8]" />}
          </h1>
          <RoleTag agent={agent} />
        </div>
      </div>

      <h2 className="mt-8 font-display text-xl text-[#2C3830]">Listings ({listings.length})</h2>
      {listings.length === 0 ? (
        <p className="mt-4 text-sm text-[#6B7280]">No active listings.</p>
      ) : (
        <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {listings.map((l) => <PropertyCard key={l.id} listing={l} />)}
        </div>
      )}
    </div>
  );
}
